import { DatePipe } from '@angular/common';
import {
  Component,
  ElementRef,
  OnInit,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  untracked,
  viewChild
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import {
  Day as DateFnsDay,
  eachDayOfInterval,
  endOfMonth,
  isEqual,
  startOfMonth,
  startOfToday,
  startOfWeek
} from 'date-fns';

import { parseDate } from '@core/utils/date-utils';
import { isNil } from '@core/utils/shared-utils';

import { WindowValues } from '@shared/components/table/table-cell/utils';

interface Day {
  day: Date;
  isToday: boolean;
  isPartOfCurrentMonth: boolean;
}

export type DateCellEditorValue = Date | WindowValues<Date> | undefined;

@Component({
  selector: 'app-datepicker-dropdown',
  imports: [DatePipe],
  templateUrl: './datepicker-dropdown.component.html',
  styleUrl: './datepicker-dropdown.component.css'
})
export class DatepickerDropdownComponent implements OnInit {
  type = input<'date' | 'datetime'>('date');
  window = input<boolean>(false);
  actualValue = input<DateCellEditorValue>();
  minDate = input<Date | undefined>();
  maxDate = input<Date | undefined>();

  selectedValue = signal<DateCellEditorValue>(this.actualValue());
  focusedMonth = signal<Date>(new Date());
  selectedInputIndex = signal<number>(0);

  updatedValue = output<DateCellEditorValue>();

  constructor() {
    effect(() => {
      // Watch for selectedValue changes and refocus
      this.selectedValue(); // Trigger reactivity

      // Use untracked to avoid infinite loops since focus() doesn't change signals
      untracked(() => {
        this.focus();
      });
    });
  }

  // ViewChild reference to the first input element
  private firstInput = viewChild<ElementRef<HTMLInputElement>>('firstInput');

  private readonly today = startOfToday();
  private readonly translate = inject(TranslateService);
  protected readonly range = computed(() =>
    Array.from({ length: this.window() ? 2 : 1 }, (_, i) => i)
  );

  protected readonly startWeekDay = computed(() => {
    return startOfWeek(new Date()).getDay();
  });
  protected readonly monthWeeks = computed(() => this.getMonthWeeks());

  protected readonly currentLang = computed(() => {
    return this.translate.getCurrentLang();
  });

  protected readonly weekDays = computed(() => {
    const days = this.translate.instant('common.calendar.week_days').split(',');
    const res: string[] = [];
    for (let i = 0; i < days.length; i++) {
      res[i] = days[(this.startWeekDay() + i) % 7].slice(0, 2);
    }
    return res || [];
  });

  protected readonly days = computed(() => {
    return eachDayOfInterval({
      start: startOfMonth(this.focusedMonth()),
      end: endOfMonth(this.focusedMonth())
    });
  });

  /** Public method to focus the first input element in the dropdown */
  public focus(): void {
    this.firstInput()?.nativeElement.focus();
  }

  ngOnInit() {
    if (isNil(this.actualValue())) {
      if (this.window()) this.selectedValue.set({ start: this.today, finish: this.today });
      else this.selectedValue.set(this.today);
    }
  }

  private getMonthWeeks(): Day[][] {
    const days = this.days();
    const weeks: Day[][] = [];
    let currentWeek: Day[] = [];

    const firstDayOfWeek = startOfWeek(days[0], {
      weekStartsOn: this.startWeekDay() as DateFnsDay
    });
    for (let d = firstDayOfWeek; d < days[0]; d.setDate(d.getDate() + 1)) {
      currentWeek.push({
        day: new Date(d),
        isToday: isEqual(new Date(d), this.today),
        isPartOfCurrentMonth: false
      });
    }

    for (const day of days) {
      currentWeek.push({
        day,
        isToday: isEqual(day, this.today),
        isPartOfCurrentMonth: true
      });
      if (currentWeek.length === 7) {
        weeks.push(currentWeek);
        currentWeek = [];
      }
    }

    if (currentWeek.length === 0) {
      return weeks;
    }

    const lastDayOfWeek = days[days.length - 1];
    const firstNewWeekDay = new Date(lastDayOfWeek);
    firstNewWeekDay.setDate(firstNewWeekDay.getDate() + 1);
    for (
      let d = new Date(firstNewWeekDay);
      currentWeek.length < 7;
      d.setDate(d.getDate() + 1)
    ) {
      currentWeek.push({
        day: new Date(d),
        isToday: isEqual(new Date(d), this.today),
        isPartOfCurrentMonth: false
      });
    }
    if (currentWeek.length > 0) {
      weeks.push(currentWeek);
    }

    return weeks;
  }

  protected trackMonthWeek(index: number, item: Day[]): string {
    return `week_${item[0].day.getTime()}_${index}`;
  }

  protected trackMonthDay(index: number, item: Day): string {
    return `${item.day.getTime()}_${index}`;
  }

  protected trackWeekDayString(index: number, item: string): string {
    return `${item}_${index}`;
  }

  private normalizeDateToDay(date: Date): Date {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  }

  protected isInSelectedRange(day: Date): boolean {
    const value = this.selectedValue();

    if (typeof value === 'object' && 'start' in value && 'finish' in value) {
      const normalizedDay = this.normalizeDateToDay(day);
      const normalizedStart = this.normalizeDateToDay(value.start);
      const normalizedEnd = this.normalizeDateToDay(value.finish);
      return normalizedDay >= normalizedStart && normalizedDay <= normalizedEnd;
    }

    return false;
  }

  protected isFirstInSelectedRange(day: Date): boolean {
    const value = this.selectedValue();
    const normalizedDay = this.normalizeDateToDay(day);

    if (typeof value === 'object' && 'start' in value) {
      const normalizedStart = this.normalizeDateToDay(value.start);
      return isEqual(normalizedDay, normalizedStart);
    } else if (value instanceof Date) {
      const normalizedValue = this.normalizeDateToDay(value);
      return isEqual(normalizedDay, normalizedValue);
    }
    return false;
  }

  protected isLastInSelectedRange(day: Date): boolean {
    const value = this.selectedValue();
    if (typeof value === 'object' && 'finish' in value) {
      const normalizedDay = this.normalizeDateToDay(day);
      const normalizedEnd = this.normalizeDateToDay(value.finish);
      return isEqual(normalizedDay, normalizedEnd);
    }
    return false;
  }

  protected getDayClasses(day: Day): string {
    let classes = '';

    if (this.isFirstInSelectedRange(day.day) || this.isLastInSelectedRange(day.day)) {
      classes += ' bg-blue-500 text-white font-medium rounded-md';
    } else if (!day.isPartOfCurrentMonth) {
      classes +=
        ' text-zinc-400 dark:text-zinc-600 cursor-not-allowed hover:bg-transparent rounded-md';
    } else if (day.isToday) {
      classes += ' text-white bg-red-500 font-medium rounded-full';
    } else {
      classes += ' text-zinc-700 dark:text-zinc-300 rounded-md';
    }

    return classes;
  }

  protected getClassesForPart(key: number) {
    const base =
      'flex items-center rounded-md grow px-1.5 bg-zinc-100 dark:bg-zinc-800 h-7';

    if (!this.window()) return base;

    if (this.selectedInputIndex() === key) {
      return `${base} border-2 border-blue-500`;
    }

    return base;
  }

  protected onInputChange($event: Event, key: number, type: 'date' | 'time' = 'date') {
    const input = $event.target as HTMLInputElement;

    if (type === 'date') {
      const parsedDate = parseDate(input.value);

      if (isNil(parsedDate)) {
        return;
      }

      const currentValue = this.selectedValue();
      if (this.window()) {
        if (
          typeof currentValue === 'object' &&
          'start' in currentValue &&
          'finish' in currentValue
        ) {
          const firstDate = currentValue.start;
          const secondDate = currentValue.finish;

          if (key === 0) {
            parsedDate.setHours(firstDate.getHours(), firstDate.getMinutes());
            this.selectedValue.set({ start: parsedDate, finish: secondDate });
          } else {
            parsedDate.setHours(secondDate.getHours(), secondDate.getMinutes());
            this.selectedValue.set({ start: firstDate, finish: parsedDate });
          }
        }
      } else {
        if (currentValue instanceof Date)
          parsedDate.setHours(currentValue.getHours(), currentValue.getMinutes());

        this.selectedValue.set(parsedDate);
      }
    } else {
      const timeParts = input.value.split(':');

      if (timeParts.length !== 2) {
        return;
      }

      const hours = parseInt(timeParts[0], 10);
      const minutes = parseInt(timeParts[1], 10);
      if (isNaN(hours) || isNaN(minutes)) {
        return;
      }

      const currentValue = this.selectedValue();
      if (this.window()) {
        if (
          typeof currentValue === 'object' &&
          'start' in currentValue &&
          'finish' in currentValue
        ) {
          const firstDate = currentValue.start;
          const secondDate = currentValue.finish;

          if (key === 0) {
            const newDate = new Date(firstDate);
            newDate.setHours(hours, minutes);
            this.selectedValue.set({ start: newDate, finish: secondDate });
          } else {
            const newDate = new Date(secondDate);
            newDate.setHours(hours, minutes);
            this.selectedValue.set({ start: firstDate, finish: newDate });
          }
        }
      } else if (currentValue instanceof Date) {
        const newDate = new Date(currentValue);
        newDate.setHours(hours, minutes);
        this.selectedValue.set(newDate);
      }
    }

    this.updatedValue.emit(this.selectedValue());
  }

  protected getInputValue(key: number, type: 'date' | 'time' = 'date'): string {
    const value = this.selectedValue();
    const locale = this.translate.getCurrentLang();
    const datePipe = new DatePipe(locale);

    const formatFor = (d: Date) => {
      if (type === 'date') {
        return datePipe.transform(d, 'shortDate');
      }
      return datePipe.transform(d, 'shortTime');
    };

    if (typeof value === 'object' && 'start' in value && 'finish' in value) {
      return formatFor(key === 0 ? value.start : value.finish) || '';
    } else if (value instanceof Date && key === 0) {
      return formatFor(value) || '';
    }
    return '';
  }

  protected onSelectPart(key: number) {
    if (this.window()) this.selectedInputIndex.set(key);
  }

  protected onDayClick(day: Date) {
    const currentMonth = this.focusedMonth();
    if (
      day.getMonth() !== currentMonth.getMonth() ||
      day.getFullYear() !== currentMonth.getFullYear()
    ) {
      this.focusedMonth.set(new Date(day.getFullYear(), day.getMonth(), 1));
    }

    if (this.window()) {
      const currentValue = this.selectedValue();

      if (
        typeof currentValue === 'object' &&
        'start' in currentValue &&
        'finish' in currentValue
      ) {
        const firstDate = currentValue.start;
        const secondDate = currentValue.finish;

        const normalizedDay = this.normalizeDateToDay(day);
        const normalizedFirstDate = this.normalizeDateToDay(firstDate);
        const normalizedSecondDate = this.normalizeDateToDay(secondDate);

        if (normalizedDay < normalizedFirstDate) {
          this.selectedInputIndex.set(0);
        } else if (normalizedDay > normalizedSecondDate) {
          this.selectedInputIndex.set(1);
        }

        if (this.selectedInputIndex() === 0) {
          day.setHours(firstDate.getHours(), firstDate.getMinutes());
          this.selectedValue.set({ start: day, finish: secondDate });
        } else {
          day.setHours(secondDate.getHours(), secondDate.getMinutes());
          this.selectedValue.set({ start: firstDate, finish: day });
        }
      }
    } else {
      const date = this.selectedValue();
      if (date instanceof Date) {
        day.setHours(date.getHours(), date.getMinutes());
      }
      this.selectedValue.set(day);
    }

    this.updatedValue.emit(this.selectedValue());
  }

  protected previousMonth() {
    const currentMonth = this.focusedMonth();
    const previousMonth = new Date(
      currentMonth.getFullYear(),
      currentMonth.getMonth() - 1,
      1
    );
    this.focusedMonth.set(previousMonth);
  }

  protected nextMonth() {
    const currentMonth = this.focusedMonth();
    const nextMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1);
    this.focusedMonth.set(nextMonth);
  }

  protected onKeyDown(event: KeyboardEvent, day: Date) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.onDayClick(day);
    }
  }

  protected onKeyInput(e: KeyboardEvent) {
    // Only stop propagation for arrow keys - they should navigate within the picker
    // Allow Enter, Escape, Tab to propagate to parent for commit/cancel logic
    const arrowKeys = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'];
    if (arrowKeys.includes(e.key)) {
      e.stopPropagation();
    }
  }
}
